import React, { useEffect, useState } from 'react';
import { 
  Typography, 
  Table, 
  Button, 
  Space, 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Input, 
  Tag, 
  Alert,
  message,
  Badge,
  Tooltip
} from 'antd';
import {
  FilterOutlined
} from '@ant-design/icons';
import { inventoryService, InventoryItem } from '../../services/inventoryService';
import { InventoryDetailModal, ExpiringMedicinesModal, LowStockModal } from '../../components/inventory';
import { formatQuantity } from '../../utils/formatters';
import styles from '../inventory/InventoryPage.module.css';

const { Title, Text } = Typography;
const { Search } = Input;

const InventoryPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [filteredInventory, setFilteredInventory] = useState<InventoryItem[]>([]);
  const [searchText, setSearchText] = useState('');
  const [selectedMedicineId, setSelectedMedicineId] = useState<number | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [expiringModalVisible, setExpiringModalVisible] = useState(false);
  const [lowStockModalVisible, setLowStockModalVisible] = useState(false);

  useEffect(() => {
    loadInventory();
  }, []);

  useEffect(() => {
    filterInventory();
  }, [inventory, searchText]);

  const loadInventory = async () => {
    setLoading(true);
    try {
      const data = await inventoryService.getInventory();
      setInventory(data);
    } catch (error) {
      console.error('Error loading inventory:', error);
      message.error('Có lỗi xảy ra khi tải dữ liệu tồn kho');
    } finally {
      setLoading(false);
    }
  };

  const filterInventory = () => {
    if (!searchText.trim()) {
      setFilteredInventory(inventory);
      return;
    }

    const filtered = inventory.filter(item =>
      item.name.toLowerCase().includes(searchText.toLowerCase()) ||
      item.code.toLowerCase().includes(searchText.toLowerCase())
    );
    setFilteredInventory(filtered);
  };

  const handleViewDetails = (medicineId: number) => {
    setSelectedMedicineId(medicineId);
    setDetailModalVisible(true);
  };

  const getStockStatus = (item: InventoryItem) => {
    if (item.total_quantity === 0) {
      return <Tag color="red">Hết hàng</Tag>;
    }
    if (item.total_quantity < item.min_stock_level) {
      return <Tag color="orange">Tồn kho thấp</Tag>;
    }
    return <Tag color="green">Ổn định</Tag>;
  };

  const columns = [
    {
      title: 'Thuốc',
      key: 'medicine_info',
      width: 200,
      fixed: 'left' as const,
      sorter: (a: InventoryItem, b: InventoryItem) => a.name.localeCompare(b.name),
      render: (_: any, record: InventoryItem) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{ 
            fontWeight: '600', 
            fontSize: '15px',
            color: '#1f2937',
            lineHeight: '1.2'
          }}>
            {record.name}
          </div>
          <div style={{ 
            fontSize: '12px', 
            color: '#6b7280',
            fontWeight: '400'
          }}>
            Mã: {record.code}
          </div>
        </div>
      ),
    },
    {
      title: 'Tồn kho',
      key: 'stock_info',
      width: 160,
      render: (_: any, record: InventoryItem) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '13px',
            color: '#374151'
          }}>
            <span style={{ fontWeight: '500' }}>Hiện tại:</span>
            <span style={{ fontWeight: '600', color: '#1f2937' }}>
              {formatQuantity(record.total_quantity, record.unit)}
            </span>
          </div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '13px',
            color: '#374151'
          }}>
            <span style={{ fontWeight: '500' }}>Tối thiểu:</span>
            <span style={{ fontWeight: '500', color: '#6b7280' }}>
              {formatQuantity(record.min_stock_level, record.unit)}
            </span>
          </div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '12px',
            color: '#6b7280',
            borderTop: '1px solid #e5e7eb',
            paddingTop: '4px'
          }}>
            <span style={{ fontWeight: '500' }}>Số lô:</span>
            <span style={{ fontWeight: '500' }}>{record.lot_count}</span>
          </div>
        </div>
      ),
    },
    {
      title: 'Trạng thái',
      key: 'status',
      width: 120,
      sorter: (a: InventoryItem, b: InventoryItem) => {
        const getStatusOrder = (item: InventoryItem) => {
          if (item.total_quantity === 0) return 0; // Hết hàng
          if (item.total_quantity < item.min_stock_level) return 1; // Tồn kho thấp
          return 2; // Ổn định
        };
        return getStatusOrder(a) - getStatusOrder(b);
      },
      render: (_: any, record: InventoryItem) => {
        const statusConfig = {
          outOfStock: { 
            color: '#dc2626', 
            bgColor: '#fee2e2',
            text: 'Hết hàng',
            icon: 'fas fa-times-circle'
          },
          lowStock: { 
            color: '#f59e0b', 
            bgColor: '#fef3c7',
            text: 'Tồn kho thấp',
            icon: 'fas fa-exclamation-triangle'
          },
          stable: { 
            color: '#059669', 
            bgColor: '#d1fae5',
            text: 'Ổn định',
            icon: 'fas fa-check-circle'
          }
        };
        
        let config;
        if (record.total_quantity === 0) {
          config = statusConfig.outOfStock;
        } else if (record.total_quantity < record.min_stock_level) {
          config = statusConfig.lowStock;
        } else {
          config = statusConfig.stable;
        }
        
        return (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '6px',
            padding: '6px 12px',
            borderRadius: '20px',
            backgroundColor: config.bgColor,
            color: config.color,
            fontSize: '12px',
            fontWeight: '500',
            width: 'fit-content'
          }}>
            <i className={config.icon} style={{ fontSize: '10px' }} />
            {config.text}
          </div>
        );
      },
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 100,
      fixed: 'right' as const,
      render: (_: any, record: InventoryItem) => (
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          <Tooltip title="Xem chi tiết">
            <Button
              type="text"
              icon={<i className="fas fa-eye" />}
              onClick={() => handleViewDetails(record.id)}
              size="small"
              style={{ 
                color: '#6b7280',
                border: 'none',
                boxShadow: 'none'
              }}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  const getSummaryStats = () => {
    const totalMedicines = inventory.length;
    const outOfStock = inventory.filter(item => item.total_quantity === 0).length;
    const lowStock = inventory.filter(item => 
      item.total_quantity > 0 && item.total_quantity < item.min_stock_level
    ).length;
    const totalQuantity = inventory.reduce((sum, item) => sum + item.total_quantity, 0);

    return (
      <div className={styles.statsContainer}>
        <Row gutter={[24, 16]} className={styles.statsRow}>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} hoverable>
              <div className={styles.statContent}>
                <div className={styles.statIcon} style={{ background: '#3b82f6' }}>
                  <i className="fas fa-pills" />
                </div>
                <div className={styles.statInfo}>
                  <Statistic
                    title="Tổng số thuốc"
                    value={totalMedicines}
                    suffix="loại"
                    valueStyle={{ color: '#1f2937', fontSize: '24px', fontWeight: 'bold' }}
                  />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} hoverable>
              <div className={styles.statContent}>
                <div className={styles.statIcon} style={{ background: '#10b981' }}>
                  <i className="fas fa-boxes" />
                </div>
                <div className={styles.statInfo}>
                  <Statistic
                    title="Tổng tồn kho"
                    value={totalQuantity}
                    suffix="đơn vị"
                    valueStyle={{ color: '#1f2937', fontSize: '24px', fontWeight: 'bold' }}
                  />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} hoverable>
              <div className={styles.statContent}>
                <div className={styles.statIcon} style={{ background: '#ef4444' }}>
                  <i className="fas fa-exclamation-triangle" />
                </div>
                <div className={styles.statInfo}>
                  <Statistic
                    title="Hết hàng"
                    value={outOfStock}
                    suffix="loại"
                    valueStyle={{ color: '#dc2626', fontSize: '24px', fontWeight: 'bold' }}
                  />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} hoverable>
              <div className={styles.statContent}>
                <div className={styles.statIcon} style={{ background: '#f59e0b' }}>
                  <i className="fas fa-exclamation-circle" />
                </div>
                <div className={styles.statInfo}>
                  <Statistic
                    title="Tồn kho thấp"
                    value={lowStock}
                    suffix="loại"
                    valueStyle={{ color: '#ea580c', fontSize: '24px', fontWeight: 'bold' }}
                  />
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  const getAlerts = () => {
    const outOfStock = inventory.filter(item => item.total_quantity === 0);

    if (outOfStock.length === 0) {
      return null;
    }

    return (
      <div className={styles.alertSection}>
        <Alert
          message={
            <div className={styles.alertHeader}>
              <Badge count={outOfStock.length} size="small" />
              <span style={{ marginLeft: 8 }}>Có {outOfStock.length} loại thuốc hết hàng</span>
            </div>
          }
          description={
            <div className={styles.alertDescription}>
              <Text>Danh sách: {outOfStock.map(item => item.name).join(', ')}</Text>
            </div>
          }
          type="error"
          showIcon
          icon={<i className="fas fa-exclamation-circle" />}
          className={styles.alertCard}
          action={
            <Button size="small" type="text" onClick={() => setLowStockModalVisible(true)}>
              Xem chi tiết
            </Button>
          }
        />
      </div>
    );
  };

  return (
    <div className={styles.container}>
      {getSummaryStats()}
      {getAlerts()}

      <Card className={styles.tableCard}>
        <div className={styles.tableHeader}>
          <div className={styles.searchSection}>
            <Search
              placeholder="Tìm kiếm theo tên hoặc mã thuốc..."
              allowClear
              className={styles.searchInput}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={filterInventory}
              prefix={<i className="fas fa-search" />}
              size="large"
            />
          </div>
          <div className={styles.actionButtons}>
            <Space size="middle">
              <Tooltip title="Làm mới dữ liệu">
                <Button
                  icon={<i className="fas fa-sync-alt" />}
                  onClick={loadInventory}
                  loading={loading}
                  className={styles.actionButton}
                >
                  Làm mới
                </Button>
              </Tooltip>
              <Tooltip title="Xem thuốc tồn kho thấp">
                <Button
                  icon={<i className="fas fa-exclamation-triangle" />}
                  onClick={() => setLowStockModalVisible(true)}
                  className={styles.actionButton}
                >
                  Tồn kho thấp
                </Button>
              </Tooltip>
              <Tooltip title="Xem thuốc sắp hết hạn">
                <Button
                  type="primary"
                  icon={<i className="fas fa-calendar-times" />}
                  onClick={() => setExpiringModalVisible(true)}
                  className={styles.primaryButton}
                >
                  Thuốc sắp hết hạn
                </Button>
              </Tooltip>
            </Space>
          </div>
        </div>

        <Table
          dataSource={filteredInventory}
          columns={columns}
          rowKey="id"
          loading={loading}
          className={styles.inventoryTable}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} của ${total} loại thuốc`,
            className: styles.tablePagination
          }}
          size="middle"
          scroll={{ x: 600 }}
        />
      </Card>

      <InventoryDetailModal
        visible={detailModalVisible}
        medicineId={selectedMedicineId}
        onClose={() => {
          setDetailModalVisible(false);
          setSelectedMedicineId(null);
        }}
      />

      <LowStockModal
        visible={lowStockModalVisible}
        onClose={() => setLowStockModalVisible(false)}
        onViewDetails={(medicineId) => {
          setLowStockModalVisible(false);
          setSelectedMedicineId(medicineId);
          setDetailModalVisible(true);
        }}
      />

      <ExpiringMedicinesModal
        visible={expiringModalVisible}
        onClose={() => setExpiringModalVisible(false)}
      />
    </div>
  );
};

export default InventoryPage; 